import {
  type HubDeletedJob,
  type HubJob,
  HubJobKind,
  type HubJobMessage,
  type HubJobStatus,
  type HubLogLevel,
  type HubStartJob,
  type HubUpdateJobMetadata,
  type RequestParams,
} from '@robovision/hub'
import {
  addOrReplaceByKey,
  distinct,
  groupBy,
  HttpStatusCode,
  mapToObject,
  mergeArraysByKey,
  sortByKey,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  toRefs,
} from 'vue'
import { ApiErrorId } from '~domain/ApiErrorId'
import { JobV2 } from '~domain/jobV2/Job'
import type { JobArtifacts } from '~domain/jobV2/JobArtifacts'
import { JobCreator } from '~domain/jobV2/JobCreator'
import type { JobDependencies } from '~domain/jobV2/JobDependencies'
import { ability } from '~services/ability'
import { hubApi } from '~services/api'
import { queryClient } from '~services/query-client'
import {
  acceptStatus,
} from '~utils/api'
import { RETHROW_ERROR_AFTER_HANDLING } from '~utils/api/error-handling'
import { useAlgorithmsStore } from './useAlgorithmsStore'
import { useCameraConfigurationsStore } from './useCameraConfigurationsStore'
import { useDatasetsStore } from './useDatasetsStore'
import { useParametersStore } from './useParametersStore'
import { useResourcesStore } from './useResourcesStore'
import { useTagsStore } from './useTagsStore'


interface JobsState {
  apiEndpoints: Map<string, { apiUrl: string }>
  artifacts: Record<string, JobArtifacts>
  creators: Record<string, JobCreator>
  datasetJobs: Map<string, string[]>
  dependencies: Record<string, JobDependencies>
  jobs: JobV2[]
  messages: Record<string, HubJobMessage[]>
  recentlyCompleted: Record<string, Partial<Record<HubJobKind, string[]>>> // { [projectId]: { [HubJobKind]: jobId[] } }
}

interface JobFilters {
  definitionIds?: string[]
  kinds?: HubJobKind[]
  predicate?: (job: JobV2) => boolean
  projectId?: string
}

export const useJobsStore = defineStore('jobs', () => {
  const {
    getAlgorithmDefinitionByJob,
    loadAlgorithmDefinition,
  } = useAlgorithmsStore()
  const { loadProjectTags } = useTagsStore()
  const { setDatasets } = useDatasetsStore()
  const { loadParameters } = useParametersStore()
  const {
    setBlobs,
    setModels,
  } = useResourcesStore()
  const { loadCameraConfiguration } = useCameraConfigurationsStore()

  const state = reactive<JobsState>({
    apiEndpoints: new Map(),
    artifacts: {},
    creators: {},
    datasetJobs: new Map(),
    dependencies: {},
    jobs: [],
    messages: {},
    recentlyCompleted: {},
  }) as JobsState

  const getters = {
    getDatasetJobs (datasetId: string): JobV2[] {
      const jobIds = state.datasetJobs.get(datasetId) ?? []
      return jobIds.map(id => this.getJob(id)).filter(Boolean)
    },

    getJob (id: string): JobV2 | undefined {
      return state.jobs.find(j => j.id === id)
    },

    getJobApiEndpoints (jobId: string): { apiUrl: string } | undefined {
      return state.apiEndpoints.get(jobId)
    },

    getJobArtifacts (id: string): JobArtifacts {
      return state.artifacts[id] ?? {}
    },

    getJobCreator (id: string): JobCreator | undefined {
      return state.creators[id]
    },

    getJobDependencies (id: string): JobDependencies {
      return state.dependencies[id] ?? {}
    },

    getJobLogsURL (job: JobV2): string {
      const url = `jobs/${job.id}/logs`
      return hubApi.instance.getUri({ url })
    },

    getJobMessages (id: string): HubJobMessage[] {
      return state.messages[id] ?? []
    },

    getJobsByDefinition (definitionIds: string[]): JobV2[] {
      return state.jobs.filter(j => definitionIds.includes(j.jobDefinitionId))
    },

    getJobsFiltered ({
      definitionIds,
      kinds,
      predicate,
      projectId,
    }: JobFilters): JobV2[] {
      const jobs = state.jobs.filter(j => {
        return !!(
          (!projectId || j.projectId === projectId) &&
          (definitionIds?.includes(j.jobDefinitionId) ?? true) &&
          (kinds?.includes(j.kind) ?? true) &&
          (!predicate || !!predicate(j))
        )
      })
      return sortByKey(jobs, 'createdAt', false)
    },

    getRecentlyCompleted (projectId: string, kinds: HubJobKind[]): string[] {
      const jobs = state.recentlyCompleted[projectId] ?? {}
      return kinds.flatMap(kind => jobs[kind] ?? [])
    },

    hasJobsInProgress (projectId: string, kinds: HubJobKind[]): boolean {
      const jobs = this.getJobsFiltered({
        kinds,
        predicate: j => j.isInProgress,
        projectId,
      })
      return !!jobs.length
    },
  }

  const actions = {
    addRecentlyCompleted (job: JobV2): void {
      if (!state.recentlyCompleted[job.projectId]) {
        state.recentlyCompleted = {
          ...state.recentlyCompleted,
          [job.projectId]: {},
        }
      }

      if (!state.recentlyCompleted[job.projectId]?.[job.kind]) {
        state.recentlyCompleted[job.projectId] = {
          ...state.recentlyCompleted[job.projectId],
          [job.kind]: [],
        }
      }

      const existingMetrics = state.recentlyCompleted[job.projectId]?.[job.kind]
      if (existingMetrics) {
        state.recentlyCompleted[job.projectId] = {
          ...state.recentlyCompleted[job.projectId],
          [job.kind]: [
            ...existingMetrics.filter(id => id !== job.id),
            job.id,
          ],
        }
      }
    },

    async checkJobExists (jobId: string): Promise<JobV2 | undefined> {
      const job = getters.getJob(jobId)
      if (job) return job

      const response = await hubApi.jobs.getJob(
        jobId,
        { validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND) },
      )
      if (!response?.data || response.data.id === ApiErrorId.JobNotFound) return undefined

      return JobV2.fromResponse(response.data)
    },

    async deleteJobs (jobIds: string[]): Promise<void> {
      await Promise.all(jobIds.map((id) => hubApi.jobs.deleteJob(id)))

      state.jobs = state.jobs.filter((v) => !jobIds.includes(v.id))
    },

    handleDeleteJob (deletedJob: HubDeletedJob) {
      state.jobs = state.jobs.filter((job) => job.id !== deletedJob.id)
      if (state.dependencies[deletedJob.id]) {
        delete state.dependencies[deletedJob.id]
      }
    },

    async handleJob (data: HubJob): Promise<void> {
      const job = JobV2.fromResponse(data)

      state.jobs = addOrReplaceByKey(state.jobs, job, 'id')

      // EventSource returns DONE status twice, so we need that to avoid the duplicated API calls
      if (job.isFinished && !state.recentlyCompleted?.[job.projectId]?.[job.kind]?.includes(job.id)) {
        void this.loadJobArtifacts(job.id, job.kind === HubJobKind.Custom)
        const algorithmDefinition = getAlgorithmDefinitionByJob(job)
        const jobDefinition = algorithmDefinition?.getJobDefinitionById(job.jobDefinitionId)
        const hasTagArtifacts = Object.keys(jobDefinition?.tagArtifacts ?? {}).length > 0
        const hasExampleArtifacts = Object.keys(jobDefinition?.exampleArtifacts ?? {}).length > 0
        const hasSampleArtifacts = Object.keys(jobDefinition?.sampleArtifacts ?? {}).length > 0
        if (!algorithmDefinition || hasTagArtifacts || hasExampleArtifacts || hasSampleArtifacts) {
          void loadProjectTags(job.projectId, {
            includeSampleCount: true,
          })
        }
        this.addRecentlyCompleted(job)
      }
    },

    handleJobMessage (message: HubJobMessage): void {
      const messages = state.messages[message.jobId] ?? []
      state.messages[message.jobId] = [...messages, message]
    },

    async loadDatasetJobs (datasetId: string, datasetDependencyIds: string[]): Promise<void> {
      if (ability.cannot('read', 'Model') || ability.cannot('read', 'Test')) return

      const { data } = await hubApi.jobs.listJobs({
        datasetDependencyFilter: datasetDependencyIds.map(v => `${v}:${datasetId}`),
      })
      const jobs = data.items.map(JobV2.fromResponse)
      await Promise.all([
        jobs.map(j => this.loadJobArtifacts(j.id)),
        jobs.map(j => this.loadJobDependencies(j.id)),
      ])

      state.datasetJobs.set(datasetId, jobs.map(j => j.id))
      state.jobs = mergeArraysByKey(state.jobs, jobs, 'id')
    },

    async loadJob (
      jobId: string,
      {
        allowNotFound = false,
        forceLoad = true,
        loadJobArtifacts = false,
      }: {
        allowNotFound?: boolean
        forceLoad?: boolean
        loadJobArtifacts?: boolean
      } = {},
    ): Promise<JobV2 | undefined> {
      const existingJob = getters.getJob(jobId)
      if (!forceLoad && !!existingJob) return existingJob

      const {
        data,
        status,
      } = await queryClient.fetchQuery({
        queryFn: () => hubApi.jobs.getJob(
          jobId,
          {
            ...RETHROW_ERROR_AFTER_HANDLING,
            ...(allowNotFound && {
              validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
            }),
          },
        ),
        queryKey: ['getJob', jobId],
      })

      if (status === HttpStatusCode.NOT_FOUND) {
        // this removes the job from the state if it's not
        // found in the server
        state.jobs = state.jobs.filter(j => j.id !== jobId)
        return undefined
      }

      // TODO trigger loading artifacts and dependencies?
      if (loadJobArtifacts) {
        await this.loadJobArtifacts(jobId, true)
      }

      const job = JobV2.fromResponse(data)
      state.jobs = addOrReplaceByKey(state.jobs, job, 'id')

      return job
    },

    async loadJobApiEndpoint (jobId: string): Promise<void> {
      if (state.apiEndpoints.has(jobId)) return
      const { data } = await hubApi.jobs.getJobApiEndpoints(jobId)

      const urlOrigin = document.location.origin

      state.apiEndpoints.set(jobId, {
        apiUrl: data.at(-1) ? `${urlOrigin}${data.at(-1)?.apiUrl ?? ''}` : '',
      })
    },

    async loadJobArtifacts (
      jobId: string,
      ignoreAbility?: boolean,
    ): Promise<void> {
      if (
        !ignoreAbility &&
        (!ability.can('read', 'Model') || !ability.can('read', 'Test'))
      ) return

      const {
        data,
        status,
      } = await hubApi.jobs.getJobArtifacts(jobId, {
        validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
      })

      const artifacts: JobArtifacts = {}

      if (status === HttpStatusCode.NOT_FOUND) {
        console.warn(`Job ${jobId} was not found. Related workflow might have been deleted.`)
      } else {
        setBlobs(Object.values(data.blobs).flat())
        artifacts.blobs = mapToObject(data.blobs, x => x.map(r => r.id))

        setDatasets(Object.values(data.datasets).flat())
        artifacts.datasets = mapToObject(data.datasets, x => x.map(r => r.id))

        setModels(Object.values(data.models).flat())
        artifacts.models = mapToObject(data.models, x => x.map(r => r.id))

        await loadParameters(Object.values(data.parameters).flat())
        artifacts.parameters = mapToObject(data.parameters, x => x.map(r => r.id))
      }

      state.artifacts[jobId] = artifacts
    },

    async loadJobCreator (jobId: string): Promise<void> {
      const { data } = await hubApi.jobs.getJobCreator(jobId)

      state.creators[jobId] = JobCreator.fromResponse(data)
    },

    async loadJobDependencies (jobId: string): Promise<void> {
      const response = await queryClient.fetchQuery({
        queryFn: () => hubApi.jobs.getJobDependencies(jobId, RETHROW_ERROR_AFTER_HANDLING),
        queryKey: ['getJobDependencies', jobId],
      })
      const data = response.data

      const dependencies: JobDependencies = {}

      setBlobs(Object.values(data.blobs))
      dependencies.blobs = mapToObject(data.blobs, r => r.id)

      setDatasets(Object.values(data.datasets).flat())
      dependencies.datasets = mapToObject(data.datasets, x => x.map(r => r.id))

      setModels(Object.values(data.models))
      dependencies.models = mapToObject(data.models, r => r.id)

      await Promise.all(Object.values(data.exampleStreams).map(r => loadCameraConfiguration(r.id)))
      dependencies.exampleStreams = mapToObject(data.exampleStreams as Record<string, { id: string }>, r => r.id)

      await loadParameters(Object.values(data.parameters))
      dependencies.parameters = mapToObject(data.parameters, r => r.id)

      state.dependencies[jobId] = dependencies
    },

    // TODO: custom jobs refactoring - remove or limit options. Postponed to 5.8
    async loadJobs (
      {
        definitionIds,
        jobIds,
        kinds,
        projectId,
        statuses,
      }: {
        definitionIds?: string[]
        jobIds?: string[]
        kinds?: HubJobKind[]
        projectId?: string
        statuses?: HubJobStatus[]
      },
      options: {
        ignoreJobArtifactsAbility?: boolean
        jobItemsLimit?: number
        loadJobArtifacts?: boolean
        loadJobCreators?: boolean
        loadJobDefinitions?: boolean
        loadJobDependencies?: boolean
      } = {},
    ): Promise<string[]> {
      const { data } = await hubApi.jobs.listJobs({
        filter: [
          ...distinct(definitionIds ?? []).map(id => `definitionId:${id}`),
          ...distinct(jobIds ?? []).map(id => `id:${id}`),
          ...distinct(kinds ?? []).map(kind => `kind:${kind}`) ?? [],
          ...distinct(statuses ?? []).map(status => `status:${status}`) ?? [],
        ],
        projectId,
      })

      const jobs = data.items.map(JobV2.fromResponse)
      state.jobs = mergeArraysByKey(state.jobs, jobs, 'id')

      const promises: Promise<unknown>[] = []
      if (options.loadJobDefinitions) {
        const groups = groupBy(jobs, 'algorithmId')
        for (const [id, jobs] of groups.entries()) {
          const versions = distinct(jobs.map(j => j.algorithmVersion))
          versions.forEach(version => promises.push(loadAlgorithmDefinition({
            id,
            version,
          })))
        }
      }

      const slicedJobs = options.jobItemsLimit ? jobs.slice(0, options.jobItemsLimit) : jobs

      await Promise.all([
        ...promises,
        ...(options.loadJobArtifacts ? slicedJobs.map(j =>
          this.loadJobArtifacts(j.id, options.ignoreJobArtifactsAbility)) : []),
        ...(options.loadJobCreators ? slicedJobs.map(j => this.loadJobCreator(j.id)) : []),
        ...(options.loadJobDependencies ? slicedJobs.map(j => this.loadJobDependencies(j.id)) : []),
      ])

      return jobs.map(j => j.id)
    },

    async loadLastJobMessage (
      jobId: string,
      logLevels?: HubLogLevel[],
      sort?: string[],
    ): Promise<void> {
      const { data } = await hubApi.jobs.listJobMessages(
        jobId,
        {
          filter: [
            ...distinct(logLevels ?? []).map(level => `logLevel:${level}`),
          ],
          limit: 1,
          sort: [...sort ?? [], '-createdAt'],
        },
        {
          validateStatus: acceptStatus(HttpStatusCode.NOT_FOUND),
        },
      )
      const message = data?.items?.[0]

      if (message) {
        state.messages[message.jobId] = [message]
      }
    },

    async renameJob (jobId: string, name: string): Promise<JobV2> {
      return this.updateJobMetadata(jobId, { name })
    },

    resetRecentlyCompleted (): void {
      // TODO should this be project specific?
      state.recentlyCompleted = {}
    },

    async startJob (job: HubStartJob, params?: RequestParams): Promise<HubJob> {
      const { data } = await hubApi.jobs.startJob(job, params)

      return data
    },

    async stopJob (jobId: string): Promise<void> {
      await hubApi.jobs.stopJob(jobId)
    },

    async updateJobMetadata (jobId: string, metadata: HubUpdateJobMetadata): Promise<JobV2> {
      const { data } = await hubApi.jobs.updateJobMetadata(jobId, metadata)
      const job = JobV2.fromResponse(data)
      state.jobs = addOrReplaceByKey(state.jobs, job, 'id')

      return job
    },
  }

  return {
    ...toRefs(state),
    ...getters,
    ...actions,
  }
})
